/**
 * Pure helpers wiring the balance chart to the ledger feed. Kept out of
 * BalanceChart.tsx because recharts does not render under jsdom — these
 * carry the testable behaviour.
 */
import type { LedgerRow } from './buildLedgerRows';

/** date → the FIRST ledger-row id on that day (rows arrive date-sorted). */
export function firstTxIdByDate(rows: LedgerRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const row of rows) if (!m.has(row.date)) m.set(row.date, row.id);
  return m;
}

const dayTime = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);

/** Resolve a chart click (snapped to the nearest day's date) to the id of the
 *  transaction CLOSEST in time — clicking anywhere in the plot selects the
 *  nearest tx, not only its exact day. Empty ledger / no date → null. On a
 *  distance tie the earlier tx wins (``txByDate`` keys arrive date-sorted). */
export function resolveChartClick(
  date: string | undefined,
  txByDate: Map<string, string>,
): string | null {
  if (!date) return null;
  const target = dayTime(date);
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const [d, id] of txByDate) {
    const dist = Math.abs(dayTime(d) - target);
    if (dist < bestDist) {
      bestDist = dist;
      bestId = id;
    }
  }
  return bestId;
}

/** Like ``resolveChartClick`` but restricted to transactions in the clicked
 *  day's OWN month, so the picked tx is always visible in the month-filtered
 *  ledger. Returns null when that month has no transactions (→ clears the
 *  selection rather than highlighting a hidden, out-of-month row). */
export function closestTxInMonth(
  date: string | undefined,
  txByDate: Map<string, string>,
): string | null {
  if (!date) return null;
  const month = date.slice(0, 7);
  const inMonth = new Map([...txByDate].filter(([d]) => d.slice(0, 7) === month));
  return resolveChartClick(date, inMonth);
}

/** The chart marker (date + balance) for the selected tx, or null.
 *
 *  The dot must sit ON the curve. The chart draws ONE end-of-day point per day,
 *  so on a day with several transactions the curve's value is the day's EOD
 *  balance — not the selected transaction's intra-day running balance (the
 *  ledger ``Kontostand``). Using the latter floats the dot off the line, so we
 *  look the day's balance up in the series and fall back to the row balance only
 *  when the date isn't in the series. */
export function markerFor(
  selectedId: string | null,
  rows: LedgerRow[],
  dailySeries?: { date: string; balanceEur: number }[],
): { date: string; balanceEur: number } | null {
  if (!selectedId) return null;
  const row = rows.find((x) => x.id === selectedId);
  if (!row) return null;
  const dayPoint = dailySeries?.find((p) => p.date === row.date);
  return { date: row.date, balanceEur: dayPoint ? dayPoint.balanceEur : row.balanceEur };
}
